import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCreateLabel, useLabels } from '../../api/labels';
import { Checkbox } from '../../components/ui/checkbox';
import { errorMessage } from '../../lib/api';
import { toastError } from '../../stores/toasts';

/** « Ajouter un libellé » : coche/décoche les libellés et permet d'en créer à la volée. */
export function LabelPicker({
  selected,
  onToggle,
}: {
  selected: readonly string[];
  onToggle: (labelId: string, enabled: boolean) => void;
}) {
  const { data: labels = [] } = useLabels();
  const createLabel = useCreateLabel();
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  const filtered = useMemo(
    () => labels.filter((l) => l.name.toLocaleLowerCase().includes(trimmed.toLocaleLowerCase())),
    [labels, trimmed],
  );
  const exactMatch = labels.some((l) => l.name.toLocaleLowerCase() === trimmed.toLocaleLowerCase());

  const create = () => {
    if (!trimmed || exactMatch) return;
    createLabel.mutate(trimmed, {
      onSuccess: (label) => {
        onToggle(label.id, true);
        setQuery('');
      },
      onError: (error) => toastError(errorMessage(error)),
    });
  };

  return (
    <div className="w-60 py-2 text-sm">
      <div className="px-3 pb-1 font-medium">Libellé de la note</div>
      <div className="flex items-center gap-2 px-3 py-1">
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              create();
            }
          }}
          placeholder="Saisissez le nom du libellé"
          maxLength={50}
          className="bare-input py-1 text-[13px]"
        />
        <Search size={14} className="shrink-0 text-muted" aria-hidden />
      </div>
      <div className="scrollbar-thin max-h-60 overflow-y-auto">
        {filtered.map((label) => {
          const checked = selected.includes(label.id);
          return (
            <div
              key={label.id}
              onClick={() => onToggle(label.id, !checked)}
              className="flex cursor-pointer items-center gap-3 px-3 py-1.5 hover:bg-hover"
            >
              <Checkbox
                checked={checked}
                label={label.name}
                onChange={(value) => onToggle(label.id, value)}
              />
              <span className="truncate">{label.name}</span>
            </div>
          );
        })}
      </div>
      {trimmed && !exactMatch && (
        <button
          type="button"
          onClick={create}
          className="flex w-full items-center gap-2 border-t border-line px-3 pt-2 pb-1 text-left hover:bg-hover"
        >
          <Plus size={18} className="shrink-0" /> Créer «{' '}
          <span className="truncate font-medium">{trimmed}</span> »
        </button>
      )}
    </div>
  );
}
